import React, { useState } from 'react';
import SemanticGPUCard from './SemanticGPUCard';
import { GPURecommendationsProps, GPURecommendationRequest } from '../../types/gpu';

const WORKLOAD_TEMPLATES = [
  {
    title: 'Quantitative Backtesting',
    description: 'Historical strategy testing with vectorized operations, factor analysis, and portfolio optimization',
    use_cases: ['quantitative_analysis', 'time_series_analysis'],
    min_memory: 16
  },
  {
    title: 'Risk Analysis & VaR Calculation',
    description: 'Portfolio risk modeling, stress testing, VaR/CVaR calculations, and regulatory compliance',
    use_cases: ['quantitative_analysis', 'monte_carlo_simulation'],
    min_memory: 24
  },
  {
    title: 'Options & Derivatives Pricing',
    description: 'Black-Scholes modeling, Monte Carlo simulations for exotic options, and Greeks calculation',
    use_cases: ['monte_carlo_simulation', 'quantitative_analysis'],
    min_memory: 32
  },
  {
    title: 'Real-time Market Data Processing',
    description: 'Tick data ingestion, OHLC aggregation, technical indicator calculation, and streaming analytics',
    use_cases: ['time_series_analysis', 'high_frequency_trading'],
    min_memory: 8
  },
  {
    title: 'ML Alpha Generation',
    description: 'Training predictive models for signal generation, feature engineering, and trading strategy development',
    use_cases: ['machine_learning_trading', 'deep_learning_training'],
    min_memory: 16
  },
  {
    title: 'Crypto & DeFi Analytics',
    description: 'Blockchain data analysis, MEV strategies, DeFi yield optimization, and cryptocurrency arbitrage',
    use_cases: ['crypto_analytics', 'quantitative_analysis'],
    min_memory: 12
  },
  {
    title: 'Large Language Model Training',
    description: 'Training transformer models like GPT, BERT, or custom LLMs with billions of parameters',
    use_cases: ['large_language_models', 'deep_learning_training'],
    min_memory: 40
  },
  {
    title: 'AI Model Inference',
    description: 'Deploying trained models for real-time inference and serving predictions',
    use_cases: ['inference_serving'],
    min_memory: 8
  }
];

export default function GPURecommendations({
  onGetRecommendations,
  recommendations,
  loading,
  onLaunchGPU,
  onViewDetails
}: GPURecommendationsProps) {
  const [workloadDescription, setWorkloadDescription] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<number | null>(null);
  const [budgetPerHour, setBudgetPerHour] = useState<string>('');
  const [preferredProviders, setPreferredProviders] = useState<string[]>([]);
  const [minMemoryGB, setMinMemoryGB] = useState<string>('');
  const [maxInstances, setMaxInstances] = useState(5);

  const handleTemplateSelect = (index: number) => {
    const template = WORKLOAD_TEMPLATES[index];
    setSelectedTemplate(index);
    setWorkloadDescription(template.description);
    setMinMemoryGB(template.min_memory.toString());
  };

  const handleGetRecommendations = () => {
    const selectedUseCases = selectedTemplate
      ? WORKLOAD_TEMPLATES[selectedTemplate].use_cases
      : [];

    const request: GPURecommendationRequest = {
      workload_description: workloadDescription,
      priority_use_cases: selectedUseCases,
      budget_per_hour: budgetPerHour ? parseFloat(budgetPerHour) : undefined,
      preferred_providers: preferredProviders.length > 0 ? preferredProviders : undefined,
      min_memory_gb: minMemoryGB ? parseInt(minMemoryGB) : undefined,
      max_instances: maxInstances
    };

    onGetRecommendations(request);
  };

  const toggleProvider = (provider: string) => {
    setPreferredProviders(prev =>
      prev.includes(provider)
        ? prev.filter(p => p !== provider)
        : [...prev, provider]
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">AI-Powered GPU Recommendations</h2>
        <p className="text-gray-400">
          Describe your workload and get personalized GPU recommendations optimized for your use case.
        </p>
      </div>

      {/* Recommendation Form */}
      <div className="bg-gray-800/50 rounded-xl border border-gray-700 p-6">
        {/* Workload Templates */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-white mb-3">Quick Start Templates</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {WORKLOAD_TEMPLATES.map((template, index) => (
              <button
                key={index}
                onClick={() => handleTemplateSelect(index)}
                className={`p-3 rounded-lg text-left transition-all ${
                  selectedTemplate === index
                    ? 'bg-blue-600 border-blue-500'
                    : 'bg-gray-700 border-gray-600 hover:bg-gray-600'
                } border`}
              >
                <div className="text-white font-medium text-sm mb-1">
                  {template.title}
                </div>
                <div className="text-gray-400 text-xs line-clamp-2">
                  {template.description}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Custom Description */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Workload Description
          </label>
          <textarea
            value={workloadDescription}
            onChange={(e) => setWorkloadDescription(e.target.value)}
            className="w-full bg-gray-700 text-white rounded-lg px-3 py-2 border border-gray-600 focus:border-blue-500 resize-none"
            rows={3}
            placeholder="Describe your specific workload, model size, dataset, performance requirements..."
          />
        </div>

        {/* Constraints */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Budget per Hour ($)
            </label>
            <input
              type="number"
              step="0.01"
              value={budgetPerHour}
              onChange={(e) => setBudgetPerHour(e.target.value)}
              className="w-full bg-gray-700 text-white rounded-lg px-3 py-2 border border-gray-600 focus:border-blue-500"
              placeholder="Optional"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Min Memory (GB)
            </label>
            <input
              type="number"
              value={minMemoryGB}
              onChange={(e) => setMinMemoryGB(e.target.value)}
              className="w-full bg-gray-700 text-white rounded-lg px-3 py-2 border border-gray-600 focus:border-blue-500"
              placeholder="Optional"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Max Recommendations
            </label>
            <select
              value={maxInstances}
              onChange={(e) => setMaxInstances(parseInt(e.target.value))}
              className="w-full bg-gray-700 text-white rounded-lg px-3 py-2 border border-gray-600 focus:border-blue-500"
            >
              <option value={3}>3</option>
              <option value={5}>5</option>
              <option value={10}>10</option>
            </select>
          </div>
        </div>

        {/* Provider Preferences */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-300 mb-2">
            Preferred Providers (Optional)
          </label>
          <div className="flex flex-wrap gap-2">
            {['aws', 'gcp', 'azure', 'oci', 'coreweave'].map(provider => (
              <button
                key={provider}
                onClick={() => toggleProvider(provider)}
                className={`px-3 py-1 rounded-md text-sm transition-colors ${
                  preferredProviders.includes(provider)
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {provider.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        {/* Get Recommendations Button */}
        <button
          onClick={handleGetRecommendations}
          disabled={!workloadDescription.trim() || loading}
          className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Analyzing Workload...
            </>
          ) : (
            <>
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Get AI Recommendations
            </>
          )}
        </button>
      </div>

      {/* Recommendations Results */}
      {recommendations.length > 0 && (
        <div>
          <div className="flex items-center gap-3 mb-4">
            <h3 className="text-xl font-semibold text-white">
              Recommended GPUs
            </h3>
            <span className="px-2 py-1 bg-green-600/20 text-green-400 rounded-md text-sm">
              {recommendations.length} matches
            </span>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
            {recommendations.map((gpu, index) => (
              <div key={gpu.id} className="relative">
                {/* Ranking badge */}
                <div className="absolute -top-2 -left-2 z-10 w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-bold">
                  {index + 1}
                </div>
                <SemanticGPUCard
                  gpu={gpu}
                  onLaunch={onLaunchGPU}
                  onViewDetails={onViewDetails}
                />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && recommendations.length === 0 && workloadDescription && (
        <div className="text-center py-12 bg-gray-800/30 rounded-xl border border-gray-700">
          <svg className="w-16 h-16 text-gray-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-300 mb-2">No Recommendations Yet</h3>
          <p className="text-gray-500">
            Describe your workload above to get personalized GPU recommendations.
          </p>
        </div>
      )}
    </div>
  );
}